<script setup lang="ts">
import { computed } from 'vue'

import type { WidgetWrapperMetric } from './types'
import WidgetWrapperDesktop from './WidgetWrapperDesktop.vue'
import WidgetWrapperEdit from './WidgetWrapperEdit.vue'
import WidgetWrapperMobile from './WidgetWrapperMobile.vue'

/**
 * Card shell for an analytics widget, matching the "Widget wrapper" design
 * mock (claude.ai/design): eyebrow + title, an optional metric pill, and a
 * content area. Three mutually exclusive states, same as the mock: a desktop
 * view card, a desktop edit card (drag handle, editable name, settings menu,
 * resize handles, delete confirmation), and a mobile view card. Editing is
 * desktop-only — the mock defines no mobile edit state, matching how
 * dashboard editing works today.
 *
 * This is the shell only: it renders `slot` for the widget's own chart,
 * table or map. View mode's `#actions`/`#protect` and edit mode's
 * `#settings` are bare outlets, no fallback content — every real caller has
 * an explicit opinion (real controls, or none), and omitting a slot entirely
 * is enough to hide it (see each one's own doc comment for why an empty
 * `<template>` is not the same thing). There is no "Done" button:
 * this app's dashboard editing is a single page-level toggle, not a
 * per-widget session, so a per-widget exit has nowhere to report to here —
 * a future consumer with a real per-widget edit session can add one.
 */
type Props = {
  view?: 'desktop' | 'mobile'
  mode?: 'view' | 'edit'
  sectionLabel?: string
  title?: string
  /** Eyebrow shown above the editable name in edit mode, e.g. "Pie chart". */
  widgetType?: string
  /** Editable widget name, edit mode only. */
  name?: string
  metric?: WidgetWrapperMetric[]
  /** Edit mode only. */
  protected?: boolean
  /** Edit mode only — shows the Protect/Unprotect row. On by default. */
  protectable?: boolean
  /** Edit mode only — the two discrete size flags the resize handles toggle. */
  wide?: boolean
  tall?: boolean
}

type Emits = {
  'update:name': [value: string]
  'update:protected': [value: boolean]
  'update:wide': [value: boolean]
  'update:tall': [value: boolean]
  /** Edit mode's "Duplicate widget" chosen. */
  duplicate: []
  /** Edit mode's delete confirmed. */
  delete: []
}

const {
  view = 'desktop',
  mode = 'view',
  sectionLabel = '',
  title = '',
  widgetType = '',
  name = '',
  metric,
  protected: isProtected = false,
  protectable = true,
  wide = false,
  tall = false,
} = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  name: 'DSWidgetWrapper',
})

const isEditMode = computed(() => view === 'desktop' && mode === 'edit')
</script>

<template>
  <WidgetWrapperEdit
    v-if="isEditMode"
    :widget-type="widgetType"
    :name="name"
    :protected="isProtected"
    :protectable="protectable"
    :wide="wide"
    :tall="tall"
    @update:name="emit('update:name', $event)"
    @update:protected="emit('update:protected', $event)"
    @update:wide="emit('update:wide', $event)"
    @update:tall="emit('update:tall', $event)"
    @duplicate="emit('duplicate')"
    @delete="emit('delete')"
  >
    <slot />
    <template #settings>
      <slot name="settings" />
    </template>
  </WidgetWrapperEdit>
  <WidgetWrapperMobile
    v-else-if="view === 'mobile'"
    :section-label="sectionLabel"
    :title="title"
    :metric="metric"
  >
    <slot />
    <template
      v-if="$slots.header"
      #header
    >
      <slot name="header" />
    </template>
    <template
      v-if="$slots.actions"
      #actions
    >
      <slot name="actions" />
    </template>
    <template #protect>
      <slot name="protect" />
    </template>
  </WidgetWrapperMobile>
  <WidgetWrapperDesktop
    v-else
    :section-label="sectionLabel"
    :title="title"
    :metric="metric"
  >
    <slot />
    <template
      v-if="$slots.header"
      #header
    >
      <slot name="header" />
    </template>
    <template
      v-if="$slots.actions"
      #actions
    >
      <slot name="actions" />
    </template>
    <template #protect>
      <slot name="protect" />
    </template>
  </WidgetWrapperDesktop>
</template>
